import { Icon } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import * as React from 'react';
import sanitizeHtml from 'sanitize-html';

import { SafeInnerHtmlSetter } from 'components/shared/util/SafeInnerHtmlSetter';

import '../styles/assistant-summary.less';

type AssistantSummaryItemProps = {
  category: string;
  message: string;
  action?: string;
  value?: string;
};

type StateT = {
  hasOverflow: boolean;
  isExpanded: boolean;
};

export default class AssistantSummaryItem extends React.Component<
  AssistantSummaryItemProps,
  StateT
> {
  overflowRef: React.RefObject<any>;

  constructor(props: AssistantSummaryItemProps) {
    super(props);

    this.overflowRef = React.createRef();

    this.toggleExpand = this.toggleExpand.bind(this);
    this.checkForOverflow = this.checkForOverflow.bind(this);
    this.removeTrailingNewlines = this.removeTrailingNewlines.bind(this);
    this.removeBlockTagsFromFirstLine =
      this.removeBlockTagsFromFirstLine.bind(this);
    this.prepareMessage = this.prepareMessage.bind(this);
    this.renderMessage = this.renderMessage.bind(this);

    this.state = {
      hasOverflow: false,
      isExpanded: false,
    };
  }
  componentDidMount() {
    window.addEventListener('resize', this.checkForOverflow);
    this.checkForOverflow();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.checkForOverflow);
  }

  checkForOverflow() {
    const { current } = this.overflowRef;
    if (current) {
      this.setState({
        hasOverflow: current.offsetHeight > 24, // Magic number 🔫
      });
    }
  }

  toggleExpand() {
    const { isExpanded } = this.state;
    this.setState({
      isExpanded: !isExpanded,
    });
  }

  removeTrailingNewlines(markup: string) {
    return markup ? markup.replace(/(<br\/?>)+$/, '') : '';
  }

  removeBlockTagsFromFirstLine(markup: string) {
    const tempNode = document.createElement('div');
    // TODO: [TEAMS-12076] Investigate improper use of innerHTML
    tempNode.innerHTML = markup;
    const firstElement = tempNode.firstElementChild;
    if (firstElement && ['DIV', 'P'].includes(firstElement.tagName)) {
      const spanReplacement = document.createElement('span');
      // TODO: [TEAMS-12076] Investigate improper use of innerHTML
      spanReplacement.innerHTML = firstElement.innerHTML;
      tempNode.insertBefore(spanReplacement, firstElement);
      tempNode.removeChild(firstElement);
    }
    return tempNode.innerHTML;
  }

  prepareMessage(markup: string) {
    const { isExpanded } = this.state;
    if (!markup) return '';
    const markdownWithoutLeadingBlock =
      this.removeBlockTagsFromFirstLine(markup);
    if (!isExpanded) {
      return sanitizeHtml(markdownWithoutLeadingBlock, {
        allowedTags: [],
      });
    }
    return this.removeTrailingNewlines(markdownWithoutLeadingBlock);
  }

  renderMessage() {
    const { category, message, action, value } = this.props;
    const markup = this.prepareMessage(message);
    return (
      <React.Fragment>
        <span className="assistant-summary-item__text">{category} shown</span>
        &nbsp;&quot;
        <SafeInnerHtmlSetter
          element="span"
          content={markup}
          noSanitize
          className="assistant-summary-item__text assistant-summary-item__text--crop"
        />
        &quot;
        {!!action && (
          <span className="assistant-summary-item__text">
            &nbsp;and {action}
          </span>
        )}
        {!!value && (
          <span className="assistant-summary-item__text">
            &nbsp;&quot;<strong>{value}</strong>&quot;
          </span>
        )}
      </React.Fragment>
    );
  }

  render() {
    const { hasOverflow, isExpanded } = this.state;
    const rootClass = classnames('assistant-summary-item', {
      'assistant-summary-item--expandable': hasOverflow,
      'assistant-summary-item--expanded': isExpanded,
    });

    return (
      <li className={rootClass}>
        <div className="assistant-summary-item__bullet">
          {hasOverflow && (
            <button
              onClick={this.toggleExpand}
              className="assistant-summary-item__toggle"
            >
              <Icon name="angle-right" size={16} />
            </button>
          )}
        </div>
        {this.renderMessage()}
        <div
          ref={this.overflowRef}
          className="assistant-summary-item__overflow-container"
        >
          {this.renderMessage()}
        </div>
      </li>
    );
  }
}
